import React from 'react';
import { format } from "date-fns";
import { Select, MenuItem, FormControl } from '@mui/material';

/**
 * A toolbar component for the calendar that provides navigation controls (Back, Today, Next)
 * and view toggles (Month, Week, Day). It integrates with the `react-big-calendar` library
 * to handle navigation and view changes.
 *
 * @component
 * @param {Object} toolbar - The toolbar props provided by `react-big-calendar`.
 * @param {function} toolbar.onNavigate - A function to handle calendar navigation (e.g., "PREV", "NEXT", "TODAY").
 * @param {function} toolbar.onView - A function to handle view changes (e.g., "month", "week", "day").
 * @param {Date} toolbar.date - The current date displayed in the calendar.
 * @returns {JSX.Element} The rendered toolbar component.
 * @example
 * <CalendarToolbar />
 */
const CalendarToolbar = (toolbar) => {
  /**
   * Navigates to the previous period (e.g., previous month, week, or day).
   */
  const goToBack = () => toolbar.onNavigate("PREV");

  /**
   * Navigates to the next period (e.g., next month, week, or day).
   */
  const goToNext = () => toolbar.onNavigate("NEXT");

  /**
   * Navigates to the current date (today).
   */
  const goToToday = () => toolbar.onNavigate("TODAY");

  /**
   * Formats the current date for display in the toolbar.
   */
  const label = format(toolbar.date, "dd/MM/yyyy");

  /**
   * Handles the change of view from the dropdown selector.
   */
  const handleViewChange = (event) => {
    toolbar.onView(event.target.value);
  };

  return (
    <div className="rbc-toolbar">
      <span className="rbc-btn-group">
        <button onClick={goToBack}>Back</button>
        <button onClick={goToToday}>Today</button>
        <button onClick={goToNext}>Next</button>
      </span>

      <span className="rbc-toolbar-label">{label}</span>

      {/* Dropdown for Calendar View Selection */}
      <span className="rbc-btn-group">
        <FormControl variant="outlined" size="small">
          <Select
            value={toolbar.view}
            onChange={handleViewChange}
            sx={{ height: '30px', fontSize: '0.875rem' }}
          >
            <MenuItem value="month">Month</MenuItem>
            <MenuItem value="week">Week</MenuItem>
            <MenuItem value="day">Day</MenuItem>
          </Select>
        </FormControl>
      </span>
    </div>
  );
};

export default CalendarToolbar;